/**
 * Quantita' per movimento sul periodo: ogni unita' resta se stessa, ogni riga
 * dice quante esposizioni hanno una quantita' e da dove viene (registrata,
 * ricostruita, indeterminata). I totali sono solo quelli additivi: il
 * contenitore di un complex non conta, ogni ripetizione una volta sola.
 * Con `onSelect` il nome di un movimento apre il suo andamento: la tabella e'
 * l'indice naturale da cui scegliere (H6), il menu resta per cercare.
 */
import {
	dominantQuality,
	formatQuantity,
	formatShortDay,
	movementQuantityLabel,
} from "../../lib/monitoringUtils";
import { QualityBadge, StatTile, useIsCompact } from "./primitives";

function plannedLabel(movement, t, language) {
	return movement.planned
		? `${movement.planned.sets} ${t("monColSets").toLowerCase()} · ${formatQuantity(movement.planned.reps, "reps", language)}`
		: "—";
}

/** Il nome di un movimento: un bottone quando apre l'andamento, testo altrimenti. */
function MovementName({ movement, onSelect, t }) {
	const name = movement.exercise_name || "—";
	if (!onSelect) return <div className="font-medium truncate">{name}</div>;
	return (
		<button
			type="button"
			onClick={() => onSelect(movement.key)}
			className="font-medium text-left truncate max-w-full underline decoration-dotted underline-offset-4 hover:text-volt"
			aria-label={`${t("monOpenMovementTrend")} · ${name}`}
			data-testid={`movement-open-${movement.key}`}
		>
			{name}
		</button>
	);
}

/** Sul telefono: una scheda per movimento, le stesse colonne una sotto l'altra. */
function MovementCards({ movements, onSelect, t, language }) {
	return (
		<ul className="space-y-2" data-testid="movement-cards">
			{movements.map((movement) => (
				<li key={movement.key} className="rounded-sm border border-border bg-card px-3 py-2 space-y-1">
					<div className="flex items-start justify-between gap-2">
						<div className="min-w-0">
							<MovementName movement={movement} onSelect={onSelect} t={t} />
							{movement.last_date ? (
								<div className="text-[11px] text-muted-foreground">
									{t("monWellnessLast")} {formatShortDay(movement.last_date, language)}
								</div>
							) : null}
						</div>
						<QualityBadge quality={dominantQuality(movement)} t={t} />
					</div>
					<div className="text-sm">
						{movementQuantityLabel(movement, language)}
						{movement.sets ? (
							<span className="text-muted-foreground">
								{" "}· {movement.sets} {t("monColSets").toLowerCase()}
							</span>
						) : null}
						{movement.scaled ? (
							<span className="ml-1 text-[11px] text-muted-foreground">
								({movement.scaled} {t("monScaled")})
							</span>
						) : null}
					</div>
					<div className="text-[11px] text-muted-foreground tabular-nums">
						{t("monColPlanned")}: {plannedLabel(movement, t, language)} · {movement.quantified}/{movement.exposures}{" "}
						{t("monQuantified")} · {movement.days} {t("monColDays").toLowerCase()}
					</div>
				</li>
			))}
		</ul>
	);
}

export function MovementQuantitiesTable({ movements, totals, onSelect = null, t, language }) {
	const compact = useIsCompact();
	const tiles = [];
	if (totals?.reps) tiles.push({ label: t("monTotalsReps"), value: formatQuantity(totals.reps, "reps", language) });
	if (totals?.tonnage_kg)
		tiles.push({ label: t("monTotalsTonnage"), value: formatQuantity(totals.tonnage_kg, "kg", language) });
	if (totals?.meters) tiles.push({ label: t("monTotalsMeters"), value: formatQuantity(totals.meters, "meters", language) });
	if (totals?.seconds) tiles.push({ label: t("monTotalsSeconds"), value: formatQuantity(totals.seconds, "seconds", language) });
	if (totals?.calories)
		tiles.push({ label: t("monTotalsCalories"), value: formatQuantity(totals.calories, "calories", language) });
	const head = "px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-medium whitespace-nowrap";
	return (
		<div className="space-y-4">
			{tiles.length ? (
				<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
					{tiles.map((tile) => (
						<StatTile
							key={tile.label}
							label={tile.label}
							value={tile.value}
							hint={`${totals.quantified}/${totals.exposures} ${t("monQuantified")}`}
						/>
					))}
				</div>
			) : null}
			<p className="text-xs text-muted-foreground">
				{t("monTotalsNote")}
				{onSelect && movements?.length ? ` ${t("monMovementRowHint")}` : ""}
			</p>
			{!movements?.length ? (
				<div className="rounded-sm border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
					{t("monNoMovements")}
				</div>
			) : compact ? (
				<MovementCards movements={movements} onSelect={onSelect} t={t} language={language} />
			) : (
				<div className="rounded-sm border border-border bg-card overflow-x-auto">
					<table className="w-full text-sm">
						<thead className="border-b border-border">
							<tr>
								<th className={head}>{t("monColMovement")}</th>
								<th className={head}>{t("monColCoverage")}</th>
								<th className={head}>{t("monColDays")}</th>
								<th className={head}>{t("monColQuantity")}</th>
								<th className={head}>{t("monColSets")}</th>
								<th className={head}>{t("monColPlanned")}</th>
								<th className={head}>{t("monColQuality")}</th>
							</tr>
						</thead>
						<tbody>
							{movements.map((movement) => (
								<tr key={movement.key} className="border-b border-border/60 last:border-b-0">
									<td className="px-3 py-2 align-top">
										<MovementName movement={movement} onSelect={onSelect} t={t} />
										{movement.last_date ? (
											<div className="text-[11px] text-muted-foreground">
												{t("monWellnessLast")} {formatShortDay(movement.last_date, language)}
											</div>
										) : null}
									</td>
									<td className="px-3 py-2 align-top whitespace-nowrap tabular-nums">
										{movement.quantified}/{movement.exposures}{" "}
										<span className="text-xs text-muted-foreground">{t("monQuantified")}</span>
									</td>
									<td className="px-3 py-2 align-top tabular-nums">{movement.days}</td>
									<td className="px-3 py-2 align-top whitespace-nowrap">
										{movementQuantityLabel(movement, language)}
										{movement.scaled ? (
											<span className="ml-1 text-[11px] text-muted-foreground">
												({movement.scaled} {t("monScaled")})
											</span>
										) : null}
									</td>
									<td className="px-3 py-2 align-top tabular-nums">{movement.sets || "—"}</td>
									<td className="px-3 py-2 align-top whitespace-nowrap text-muted-foreground">
										{plannedLabel(movement, t, language)}
									</td>
									<td className="px-3 py-2 align-top">
										<QualityBadge quality={dominantQuality(movement)} t={t} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
